/**
 * AssetManager.jsx
 * ----------------
 * Complete asset management UI with left panel for selection and right panel for preview/editing
 */

import React, { useState, useEffect } from 'react';
import { Upload, Trash2, Search, Image, FileText, Video, Music, File, X, Eye, EyeOff, ArrowLeft, Save } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import assetService from '../../services/assetService';
import { logger } from '../../utils/logger';
import { useAppStore } from '../../store/useAppStore';
import { ProfileIcon, ProfileMenu, ProfileMenuItem } from '../Header/ProfileMenu';
import { FaUserCircle } from 'react-icons/fa';
import { IoSettingsOutline } from 'react-icons/io5';
import { FiLogOut } from 'react-icons/fi';
import Logout from '../Logout/Logut';
import useProfileStore from '../../store/useProfileStore';
import logo from '../../assets/images/newLogo.png';

const AssetManager = ({ appId, onSelectAsset = null, selectionMode = false, wizardMode = false }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const appNameFromUrl = searchParams.get('app') || appId || '';
  const { setAppName } = useAppStore();
  const userImage = useProfileStore((state) => state.userImage);
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Upload modal states
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadDescription, setUploadDescription] = useState('');
  
  // Edit states
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editVisibility, setEditVisibility] = useState(true);
  const [hasChanges, setHasChanges] = useState(false);
  const [previewSize, setPreviewSize] = useState('large'); // small, medium, large, original

  // Set app name in store when URL parameter or appId changes
  useEffect(() => {
    if (appNameFromUrl) {
      logger.log(`📂 [AssetManager] Setting app context: ${appNameFromUrl}`);
      setAppName(appNameFromUrl);
    }
  }, [appNameFromUrl, setAppName]);

  useEffect(() => {
    if (appId) {
      loadAssets();
    }
  }, [appId]);

  useEffect(() => {
    if (selectedAsset) {
      setEditName(selectedAsset.name || '');
      setEditDescription(selectedAsset.description || '');
      setEditVisibility(selectedAsset.visible !== false);
      setHasChanges(false);
    }
  }, [selectedAsset]);

  const loadAssets = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await assetService.listAssets(appId);
      setAssets(data);
      logger.log('[AssetManager] Assets loaded', { count: data.length });
    } catch (err) {
      logger.error('[AssetManager] Failed to load assets', { error: err.message });
      setError('Failed to load assets: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files?.[0];
    if (file) {
      setUploadFile(file);
      setUploadDescription('');
      setShowUploadModal(true);
    }
    event.target.value = ''; // Reset input
  };

  const handleUpload = async () => {
    if (!uploadFile) return;

    try {
      setUploading(true);
      setError(null);
      
      const metadata = {
        description: uploadDescription,
        uploadedBy: 'user', // TODO: Get from auth context
      };

      const uploadedAsset = await assetService.uploadAsset(uploadFile, appId, metadata);
      setAssets([uploadedAsset, ...assets]);
      setSuccess(`Successfully uploaded ${uploadFile.name}`);
      setTimeout(() => setSuccess(null), 3000);
      
      logger.log('[AssetManager] Asset uploaded', { assetId: uploadedAsset.id });
      
      // Close modal and reset
      setShowUploadModal(false);
      setUploadFile(null);
      setUploadDescription('');
    } catch (err) {
      logger.error('[AssetManager] Upload failed', { error: err.message });
      setError('Upload failed: ' + err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    if (!selectedAsset || !hasChanges) return;

    try {
      setError(null);
      await assetService.updateAssetMetadata(appId, selectedAsset.id, {
        name: editName,
        description: editDescription,
        visible: editVisibility,
      });
      
      const updatedAsset = {
        ...selectedAsset,
        name: editName,
        description: editDescription,
        visible: editVisibility,
      };
      
      setAssets(assets.map(a => a.id === selectedAsset.id ? updatedAsset : a));
      setSelectedAsset(updatedAsset);
      setHasChanges(false);
      setSuccess('Asset updated successfully');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('Update failed: ' + err.message);
    }
  };

  const handleDelete = async () => {
    if (!selectedAsset) return;
    
    if (!window.confirm(`Are you sure you want to delete "${selectedAsset.name}"?`)) {
      return;
    }

    try {
      setError(null);
      await assetService.deleteAsset(appId, selectedAsset.id);
      setAssets(assets.filter(a => a.id !== selectedAsset.id));
      setSelectedAsset(null);
      setSuccess(`Successfully deleted ${selectedAsset.name}`);
      setTimeout(() => setSuccess(null), 3000);
      
      logger.log('[AssetManager] Asset deleted', { assetId: selectedAsset.id });
    } catch (err) {
      logger.error('[AssetManager] Delete failed', { error: err.message });
      setError('Delete failed: ' + err.message);
    }
  };

  const getFileIcon = (fileType) => {
    if (fileType.startsWith('image/')) return <Image className="w-5 h-5" />;
    if (fileType.startsWith('video/')) return <Video className="w-5 h-5" />;
    if (fileType.startsWith('audio/')) return <Music className="w-5 h-5" />;
    if (fileType.includes('pdf')) return <FileText className="w-5 h-5" />;
    return <File className="w-5 h-5" />;
  };

  const getFileTypeCategory = (fileType) => {
    if (fileType.startsWith('image/')) return 'image';
    if (fileType.startsWith('video/')) return 'video';
    if (fileType.startsWith('audio/')) return 'audio';
    if (fileType.includes('pdf') || fileType.includes('excel') || fileType.includes('sheet')) return 'document';
    return 'other';
  };

  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  // Filter assets
  const filteredAssets = assets.filter(asset => {
    const matchesSearch = asset.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         (asset.description || '').toLowerCase().includes(searchQuery.toLowerCase());
    const matchesType = filterType === 'all' || getFileTypeCategory(asset.fileType) === filterType;
    return matchesSearch && matchesType;
  });

  // Group assets by type for dropdown
  const assetsByType = {
    all: filteredAssets,
    image: filteredAssets.filter(a => getFileTypeCategory(a.fileType) === 'image'),
    video: filteredAssets.filter(a => getFileTypeCategory(a.fileType) === 'video'),
    audio: filteredAssets.filter(a => getFileTypeCategory(a.fileType) === 'audio'),
    document: filteredAssets.filter(a => getFileTypeCategory(a.fileType) === 'document'),
    other: filteredAssets.filter(a => getFileTypeCategory(a.fileType) === 'other'),
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#F9FAFB' }}>
      {/* Custom Navigation Bar - Only show when not in wizard mode */}
      {!wizardMode && (
        <div style={{
          padding: '12px 24px',
          background: '#050618',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
          minHeight: '5.25rem',
          position: 'sticky',
          top: 0,
          zIndex: 1000
        }}>
          {/* Left Section: MUDUMBAI Logo */}
          <div 
            style={{
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              marginRight: '32px'
            }}
            onClick={() => navigate('/')}
          >
            <img 
              src={logo} 
              alt="MUDUMBAI Logo" 
              style={{
                height: '3.5rem',
                width: 'auto',
                objectFit: 'contain'
              }}
            />
          </div>

          {/* Center Section: Page-specific content */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
            flex: 1
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}>
              <div style={{
                width: '40px',
                height: '40px',
                borderRadius: '8px',
                background: 'rgba(255, 255, 255, 0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '20px'
              }}>
                🎨
              </div>
              <div>
                <h1 style={{
                  margin: 0,
                  fontSize: '20px',
                  fontWeight: '700',
                  color: '#FFFFFF',
                  textShadow: '0 1px 2px rgba(0, 0, 0, 0.1)'
                }}>
                  Asset Manager
                </h1>
                <p style={{
                  margin: 0,
                  fontSize: '12px',
                  color: 'rgba(255, 255, 255, 0.8)'
                }}>
                  {appNameFromUrl ? `Managing assets for: ${appNameFromUrl}` : 'Please select an app from the dashboard'}
                </p>
              </div>
            </div>
          </div>

          {/* Right Section: Upload Asset, Go to Dashboard and Profile */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            {/* Upload Asset Button */}
            <label style={{
              padding: '8px 14px',
              background: 'rgba(59, 130, 246, 0.8)',
              border: '1px solid rgba(59, 130, 246, 0.5)',
              borderRadius: '8px',
              color: '#FFFFFF',
              fontSize: '13px',
              fontWeight: '600',
              cursor: uploading ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              opacity: uploading ? 0.6 : 1,
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              if (!uploading) {
                e.currentTarget.style.background = 'rgba(59, 130, 246, 1)';
                e.currentTarget.style.borderColor = 'rgba(59, 130, 246, 0.8)';
              }
            }}
            onMouseLeave={(e) => {
              if (!uploading) {
                e.currentTarget.style.background = 'rgba(59, 130, 246, 0.8)';
                e.currentTarget.style.borderColor = 'rgba(59, 130, 246, 0.5)';
              }
            }}
            >
              <Upload className="w-4 h-4" />
              {uploading ? 'Uploading...' : 'Upload Asset'}
              <input
                type="file"
                onChange={handleFileSelect}
                disabled={uploading}
                style={{ display: 'none' }}
                accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx"
              />
            </label>

            {/* Go to Dashboard Button */}
            <button
              onClick={() => navigate('/dashboard')}
              style={{
                padding: '8px 14px',
                background: 'rgba(255, 255, 255, 0.2)',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                borderRadius: '8px',
                color: '#FFFFFF',
                fontSize: '13px',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = 'rgba(255, 255, 255, 0.3)';
                e.target.style.borderColor = 'rgba(255, 255, 255, 0.5)';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'rgba(255, 255, 255, 0.2)';
                e.target.style.borderColor = 'rgba(255, 255, 255, 0.3)';
              }}
            >
              <span>🏠</span>
              <span>Go to Dashboard</span>
            </button>

            {/* Profile Icon */}
            <div style={{ marginLeft: '12px' }}>
              <ProfileMenu
                trigger={
                  <div style={{
                    width: '2.75rem',
                    height: '2.75rem',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '50%',
                    border: '2px solid rgba(255, 255, 255, 0.3)',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.5)';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.3)';
                  }}
                  >
                    <ProfileIcon userImage={userImage} />
                  </div>
                }
              >
                <ProfileMenuItem onClick={() => navigate('/userprofile')}>
                  <FaUserCircle size={22} /> Profile
                </ProfileMenuItem>
                <ProfileMenuItem onClick={() => navigate('/settings')}>
                  <IoSettingsOutline size={22} /> Setting
                </ProfileMenuItem>
                <ProfileMenuItem>
                  <FiLogOut size={20} /> <Logout />
                </ProfileMenuItem>
              </ProfileMenu>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div style={{ padding: wizardMode ? '0' : '24px' }}>
        {!wizardMode && !appNameFromUrl ? (
          <div
            style={{
              padding: '40px 20px',
              textAlign: 'center',
              maxWidth: '600px',
              margin: '0 auto',
            }}
          >
            <div
              style={{
                backgroundColor: 'white',
                borderRadius: '8px',
                padding: '40px',
                border: '2px dashed #E5E7EB',
              }}
            >
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>🎨</div>
              <h2
                style={{
                  fontSize: '20px',
                  fontWeight: '600',
                  color: '#374151',
                  marginBottom: '8px',
                }}
              >
                No App Selected
              </h2>
              <p
                style={{
                  fontSize: '14px',
                  color: '#6B7280',
                  marginBottom: '20px',
                }}
              >
                Please go back to the dashboard and select an app to manage its
                assets.
              </p>
              <button
                onClick={() => navigate('/dashboard')}
                style={{
                  padding: '10px 20px',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: 'white',
                  backgroundColor: '#3B82F6',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s',
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#2563EB';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = '#3B82F6';
                }}
              >
                Go to Dashboard
              </button>
            </div>
          </div>
        ) : (
          <>
            {/* Header */}
            <div style={{ maxWidth: '1400px', margin: '0 auto', marginBottom: '24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              {wizardMode && (
                <button
                  onClick={() => navigate('/dashboard')}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '8px 16px',
                    backgroundColor: 'white',
                    border: '1px solid #D1D5DB',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back to Dashboard
                </button>
              )}
              {!wizardMode && (
                <>
                  <h1 style={{ fontSize: '28px', fontWeight: '700', color: '#111827', margin: 0 }}>
                    Asset Manager
                  </h1>
                  <span style={{ fontSize: '14px', color: '#6B7280' }}>
                    ({assets.length} {assets.length === 1 ? 'asset' : 'assets'})
                  </span>
                </>
              )}
            </div>
          </div>

        {/* Error/Success Messages */}
        {error && (
          <div style={{
            marginTop: '16px',
            padding: '12px 16px',
            backgroundColor: '#FEE2E2',
            border: '1px solid #FCA5A5',
            borderRadius: '8px',
            color: '#991B1B',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <span>{error}</span>
            <X onClick={() => setError(null)} style={{ cursor: 'pointer' }} className="w-5 h-5" />
          </div>
        )}

        {success && (
          <div style={{
            marginTop: '16px',
            padding: '12px 16px',
            backgroundColor: '#D1FAE5',
            border: '1px solid #6EE7B7',
            borderRadius: '8px',
            color: '#065F46',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <span>{success}</span>
            <X onClick={() => setSuccess(null)} style={{ cursor: 'pointer' }} className="w-5 h-5" />
          </div>
        )}
      </div>

      {/* Main Content */}
      <div style={{ maxWidth: '1600px', margin: '0 auto', display: 'flex', gap: '24px', height: 'calc(100vh - 200px)' }}>
        {/* Left Panel - Asset Selection & Form */}
        <div style={{
          width: '450px',
          backgroundColor: 'white',
          borderRadius: '12px',
          border: '1px solid #E5E7EB',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}>
          {/* Search and Filter */}
          <div style={{ padding: '16px', borderBottom: '1px solid #E5E7EB' }}>
            <input
              type="text"
              placeholder="Search assets..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #D1D5DB',
                borderRadius: '6px',
                fontSize: '14px',
                marginBottom: '12px'
              }}
            />
            
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #D1D5DB',
                borderRadius: '6px',
                fontSize: '14px',
                backgroundColor: 'white',
                cursor: 'pointer'
              }}
            >
              <option value="all">All Types ({assetsByType.all.length})</option>
              <option value="image">Images ({assetsByType.image.length})</option>
              <option value="video">Videos ({assetsByType.video.length})</option>
              <option value="audio">Audio ({assetsByType.audio.length})</option>
              <option value="document">Documents ({assetsByType.document.length})</option>
              <option value="other">Other ({assetsByType.other.length})</option>
            </select>
          </div>

          {/* Asset Dropdown List */}
          <div style={{ 
            maxHeight: '250px', 
            overflowY: 'auto', 
            padding: '8px',
            borderBottom: selectedAsset ? '1px solid #E5E7EB' : 'none'
          }}>
            {loading ? (
              <div style={{ padding: '40px', textAlign: 'center', color: '#6B7280' }}>
                Loading assets...
              </div>
            ) : filteredAssets.length === 0 ? (
              <div style={{ padding: '40px', textAlign: 'center', color: '#6B7280' }}>
                <Upload className="w-12 h-12" style={{ margin: '0 auto 12px', opacity: 0.5 }} />
                <div style={{ fontSize: '14px' }}>No assets found</div>
              </div>
            ) : (
              filteredAssets.map(asset => (
                <div
                  key={asset.id}
                  onClick={() => setSelectedAsset(asset)}
                  style={{
                    padding: '12px',
                    marginBottom: '8px',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    backgroundColor: selectedAsset?.id === asset.id ? '#EFF6FF' : 'transparent',
                    border: selectedAsset?.id === asset.id ? '2px solid #3B82F6' : '2px solid transparent',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    if (selectedAsset?.id !== asset.id) {
                      e.currentTarget.style.backgroundColor = '#F3F4F6';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (selectedAsset?.id !== asset.id) {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }
                  }}
                >
                  {/* Thumbnail/Icon */}
                  <div style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '6px',
                    backgroundColor: '#F3F4F6',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    overflow: 'hidden'
                  }}>
                    {asset.fileType.startsWith('image/') ? (
                      <img
                        src={asset.downloadURL}
                        alt={asset.name}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    ) : (
                      <div style={{ color: '#9CA3AF' }}>
                        {getFileIcon(asset.fileType)}
                      </div>
                    )}
                  </div>

                  {/* Asset Info */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#111827',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                      {asset.name}
                    </div>
                    <div style={{
                      fontSize: '12px',
                      color: '#6B7280',
                      marginTop: '2px'
                    }}>
                      {formatFileSize(asset.size)}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Form Section */}
          {selectedAsset ? (
            <div style={{
              flex: 1,
              padding: '24px',
              overflowY: 'auto'
            }}>
              <h2 style={{ fontSize: '18px', fontWeight: '700', marginBottom: '20px', color: '#111827' }}>
                Asset Details
              </h2>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>
                  Name
                </label>
                <input
                  type="text"
                  value={editName}
                  onChange={(e) => {
                    setEditName(e.target.value);
                    setHasChanges(true);
                  }}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                />
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>
                  Description
                </label>
                <textarea
                  value={editDescription}
                  onChange={(e) => {
                    setEditDescription(e.target.value);
                    setHasChanges(true);
                  }}
                  rows={4}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '6px',
                    fontSize: '14px',
                    resize: 'vertical'
                  }}
                  placeholder="Add a description..."
                />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  color: '#374151'
                }}>
                  <input
                    type="checkbox"
                    checked={editVisibility}
                    onChange={(e) => {
                      setEditVisibility(e.target.checked);
                      setHasChanges(true);
                    }}
                    style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                  />
                  {editVisibility ? <Eye className="w-5 h-5" /> : <EyeOff className="w-5 h-5" />}
                  <span style={{ fontWeight: '600' }}>Visible</span>
                </label>
              </div>

              <div style={{
                padding: '12px',
                backgroundColor: '#F9FAFB',
                borderRadius: '6px',
                fontSize: '12px',
                color: '#6B7280',
                marginBottom: '20px'
              }}>
                <div style={{ marginBottom: '4px' }}><strong>Type:</strong> {selectedAsset.fileType}</div>
                <div style={{ marginBottom: '4px' }}><strong>Size:</strong> {formatFileSize(selectedAsset.size)}</div>
                <div><strong>Uploaded:</strong> {new Date(selectedAsset.uploadedAt).toLocaleString()}</div>
              </div>

              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  onClick={handleSave}
                  disabled={!hasChanges}
                  style={{
                    flex: 1,
                    padding: '10px',
                    backgroundColor: hasChanges ? '#10B981' : '#D1D5DB',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: hasChanges ? 'pointer' : 'not-allowed',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px'
                  }}
                >
                  <Save className="w-4 h-4" />
                  Save
                </button>

                <button
                  onClick={handleDelete}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#EF4444',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  <Trash2 className="w-4 h-4" />
                  Delete
                </button>
              </div>
            </div>
          ) : (
            <div style={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#6B7280',
              fontSize: '14px'
            }}>
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <Image className="w-12 h-12" style={{ margin: '0 auto 12px', opacity: 0.3 }} />
                <div>Select an asset above</div>
              </div>
            </div>
          )}
        </div>

        {/* Right Panel - Preview */}
        <div style={{
          flex: 1,
          backgroundColor: 'white',
          borderRadius: '12px',
          border: '1px solid #E5E7EB',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}>
          {selectedAsset ? (
            <>
              {/* Size Selector for Images and Videos */}
              {(selectedAsset.fileType.startsWith('image/') || selectedAsset.fileType.startsWith('video/')) && (
                <div style={{
                  padding: '16px',
                  borderBottom: '1px solid #E5E7EB',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px'
                }}>
                  <span style={{ fontSize: '14px', fontWeight: '600', color: '#374151' }}>
                    Preview Size:
                  </span>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    {['small', 'medium', 'large', 'original'].map((size) => (
                      <button
                        key={size}
                        onClick={() => setPreviewSize(size)}
                        style={{
                          padding: '6px 14px',
                          backgroundColor: previewSize === size ? '#3B82F6' : 'white',
                          color: previewSize === size ? 'white' : '#374151',
                          border: `1px solid ${previewSize === size ? '#3B82F6' : '#D1D5DB'}`,
                          borderRadius: '6px',
                          fontSize: '13px',
                          fontWeight: '500',
                          cursor: 'pointer',
                          textTransform: 'capitalize',
                          transition: 'all 0.2s'
                        }}
                      >
                        {size}
                      </button>
                    ))}
                  </div>
                  <div style={{ marginLeft: 'auto', fontSize: '12px', color: '#6B7280' }}>
                    {previewSize === 'small' && '200px'}
                    {previewSize === 'medium' && '400px'}
                    {previewSize === 'large' && '600px'}
                    {previewSize === 'original' && 'Original'}
                  </div>
                </div>
              )}

              {/* Preview Section */}
              <div style={{
                flex: 1,
                backgroundColor: '#F9FAFB',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'auto',
                padding: '24px'
              }}>
                {selectedAsset.fileType.startsWith('image/') ? (
                  <img
                    src={selectedAsset.downloadURL}
                    alt={selectedAsset.name}
                    style={{
                      width: previewSize === 'original' ? 'auto' : 
                             previewSize === 'small' ? '200px' :
                             previewSize === 'medium' ? '400px' : '600px',
                      maxWidth: '100%',
                      maxHeight: '100%',
                      objectFit: 'contain',
                      borderRadius: '8px',
                      boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                    }}
                  />
                ) : selectedAsset.fileType.startsWith('video/') ? (
                  <video
                    src={selectedAsset.downloadURL}
                    controls
                    style={{
                      width: previewSize === 'original' ? 'auto' : 
                             previewSize === 'small' ? '200px' :
                             previewSize === 'medium' ? '400px' : '600px',
                      maxWidth: '100%',
                      maxHeight: '100%',
                      borderRadius: '8px',
                      boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                    }}
                  />
                ) : selectedAsset.fileType.startsWith('audio/') ? (
                  <div style={{ width: '100%', maxWidth: '500px' }}>
                    <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                      <Music style={{ width: '64px', height: '64px', margin: '0 auto', color: '#9CA3AF' }} />
                      <div style={{ fontSize: '16px', fontWeight: '600', color: '#374151', marginTop: '16px' }}>
                        {selectedAsset.name}
                      </div>
                    </div>
                    <audio
                      src={selectedAsset.downloadURL}
                      controls
                      style={{ width: '100%' }}
                    />
                  </div>
                ) : (
                  <div style={{ textAlign: 'center', color: '#6B7280' }}>
                    <div style={{ fontSize: '64px', marginBottom: '16px', color: '#9CA3AF' }}>
                      {getFileIcon(selectedAsset.fileType)}
                    </div>
                    <div style={{ fontSize: '18px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>
                      {selectedAsset.name}
                    </div>
                    <div style={{ fontSize: '14px', marginBottom: '16px' }}>
                      {selectedAsset.fileType.includes('pdf') ? 'PDF Document' : 'Document'}
                    </div>
                    <a
                      href={selectedAsset.downloadURL}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        display: 'inline-block',
                        padding: '10px 20px',
                        backgroundColor: '#3B82F6',
                        color: 'white',
                        borderRadius: '8px',
                        textDecoration: 'none',
                        fontSize: '14px',
                        fontWeight: '600'
                      }}
                    >
                      Open File
                    </a>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div style={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#6B7280',
              fontSize: '16px'
            }}>
              <div style={{ textAlign: 'center' }}>
                <Eye className="w-16 h-16" style={{ margin: '0 auto 16px', opacity: 0.3 }} />
                <div>Select an asset to preview</div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Upload Modal */}
      {showUploadModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '500px',
            width: '90%'
          }}>
            <h2 style={{ fontSize: '20px', fontWeight: '700', marginBottom: '16px' }}>
              Upload Asset
            </h2>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>
                File
              </label>
              <div style={{
                padding: '12px',
                backgroundColor: '#F3F4F6',
                borderRadius: '6px',
                fontSize: '14px',
                color: '#374151'
              }}>
                {uploadFile?.name}
              </div>
            </div>

            <div style={{ marginBottom: '24px' }}>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>
                Description (Optional)
              </label>
              <textarea
                value={uploadDescription}
                onChange={(e) => setUploadDescription(e.target.value)}
                rows={3}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #D1D5DB',
                  borderRadius: '6px',
                  fontSize: '14px',
                  resize: 'vertical'
                }}
                placeholder="Add a description for this asset..."
              />
            </div>

            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={() => {
                  setShowUploadModal(false);
                  setUploadFile(null);
                  setUploadDescription('');
                }}
                disabled={uploading}
                style={{
                  flex: 1,
                  padding: '10px',
                  backgroundColor: '#F3F4F6',
                  color: '#374151',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: uploading ? 'not-allowed' : 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleUpload}
                disabled={uploading}
                style={{
                  flex: 1,
                  padding: '10px',
                  backgroundColor: '#3B82F6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: uploading ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
              >
                {uploading ? (
                  <>Uploading...</>
                ) : (
                  <>
                    <Upload className="w-4 h-4" />
                    Upload
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
          </>
        )}
      </div>
    </div>
  );
};

export default AssetManager;
